import React, { useState, useEffect, useLayoutEffect, useRef, useContext } from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import { SliderContext } from 'components/Slider';
import Styled from './styles';
import ThumbContent from './ThumbContent';
import Teardrop from './Teardrop';
import KEY_CODES from '../common/KeyCodes';

const renderDefaultTeardrop = props => <Teardrop internalProps={props} />;

export interface ThumbProps extends React.ComponentPropsWithRef<'div'> {
  /** Current translate position in pixels. */
  pos: number;
  /** Function which calculate value based on position. */
  calculateValueByPos(...args: unknown[]): number;
  /** Callback function to set thumb dragging status. */
  getIsTickerThumbDragging?(...args: unknown[]): unknown;
  /** Callback function to normalize the passed value. */
  normalizeValue?(...args: unknown[]): unknown;
  /** Value which this thumb represents. */
  value?: number;
  /** A flag indicates if Slider is aligned vertically. */
  vertical?: boolean;
  /** stepBy */
  /**
   * Content for thumb
   */
  thumbContent?(...args: unknown[]): unknown;
  /**
   * Content for Teardrop
   */
  teardropContent?(...args: unknown[]): unknown;
  /**
   * If true, the slider won't be functional, it will be greyed out, and there will be no pointer when hovering.
   */
  disabled: boolean;
  /**
   * Position information of SliderTrack
   *
   * `x`: The x coordinate of the SliderTrack's origin (typically the top-left corner of the rectangle).
   *
   * `y`: The y coordinate of the SliderTrack's origin (typically the top-left corner of the rectangle).
   *
   * `width`: The width of the SliderTrack.
   *
   * `height`: The height of the SliderTrack.
   *
   * `top`: The top coordinate value of the SliderTrack
   *
   *  right`: The right coordinate value of the SliderTrack
   *
   * `bottom`: The bottom coordinate value of the SliderTrack
   *
   * `left`: The left coordinate value of the SliderTrack
   */
  trackPosition: {
    x?: number,
    y?: number,
    width?: number,
    height?: number,
    top?: number,
    right?: number,
    bottom?: number,
    left?: number
  };
  /** Function for calculating relative position percentage based on current position in pixels. */
  calculateRatio(...args: unknown[]): { ratio: number };
  /** If true, slider will act like ticker. */
  isTicker: boolean;
  /** Function for update position state in SliderTrack. */
  setPosition(...args: unknown[]): unknown;
  /** Callback function for sending new value. */
  onChange(...args: unknown[]): unknown;
  /**
   *
   */
  calculatePosByValue(...args: unknown[]): unknown;
  /**
   * Amount that the value of the input will increment by. By default this is set to 1.
   */
  stepBy: number;
  /**
   * Custom style for thumb wrapper
   */
  style?: React.CSSProperties;
  /**
   * Props that will pass to thumb wrapper
   */
  wrapperProps?: React.ComponentPropsWithRef<any>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** If true, the thumb indicator and tearDrop will be placed on the other side of the track. */
  flipIndicatorPosition?: boolean;
  /** If true, tearDrop will stay visible when slider is ticker mode. */
  alwaysShowTeardrop?: boolean;
  /** Properties passed to ThumbContent. Useful for screen reader properties. */
  internalThumbProps?: {};
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onMouseEnter?: React.MouseEventHandler;
  /** @ignore */
  onMouseLeave?: React.MouseEventHandler;
}

/** Component to add thumb in slider. All other props will be provided to the wrapper element. */
const Thumb = React.forwardRef<HTMLElement, ThumbProps>((props: ThumbProps, ref) => getContext(({ RTL, theme }) => {
  const {
    calculateValueByPos,
    pos,
    value,
    stepBy,
    trackPosition,
    disabled,
    isTicker,
    getIsTickerThumbDragging,
    calculateRatio,
    setPosition,
    normalizeValue,
    theme: themeProp,
    calculatePosByValue,
    onChange,
    thumbContent,
    teardropContent,
    style,
    vertical,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    wrapperProps,
    flipIndicatorPosition,
    alwaysShowTeardrop,
    internalThumbProps,
    ...otherProps
  } = props;
  const { disableAnimation } = useContext(SliderContext);
  const resolvedTheme = _merge(
    {},
    theme.SLIDER.thumb,
    themeProp
  );

  const trackLength = vertical ? trackPosition.height : trackPosition.width;

  const currentPos = useRef(pos);
  const teardropAnimation = useRef('');
  const [isTickerThumbDragging, setIsTickerThumbDragging] = useState(false);
  const [thumbPressed, setThumbPressed] = useState(false);

  const [isThumbHover, setIsThumbHover] = useState(false);

  const [internalValue, setInternalValue] = useState(value);
  const passedValueRef = useRef(value);

  const validatePos = (draggedPosition) => {
    if (draggedPosition < 0) {
      return 0;
    } else if (draggedPosition > trackLength) {
      return trackLength;
    }
    return draggedPosition;
  };
  const handleMouseMove = (e) => {
    if (!disabled) {
      const rawDragPosition = vertical ? e.clientY - trackPosition.y : e.clientX - trackPosition.x;
      const draggedPosition = validatePos(rawDragPosition);
      // if is not ticker, set position when it is dragged over a step
      const ratio = vertical ? 1 - calculateRatio(draggedPosition).ratio : calculateRatio(draggedPosition).ratio;
      const newValue = calculateValueByPos(ratio * trackLength);
      if (!isTicker) {
        if (newValue !== passedValueRef.current) {
          onChange(e, newValue);
        }
      } else {
        // if is ticker, set position while dragging
        setInternalValue(newValue);
        setPosition(draggedPosition);
      }
    }
  };

  useLayoutEffect(() => {
    currentPos.current = pos;
    if (value !== passedValueRef.current) {
      setInternalValue(value);
      passedValueRef.current = value;
    }
  }, [pos, value]);

  /** when pointer up, calculate accurate thumb position based on stepby */
  const handlePointerUp = (e) => {
    if (!disabled) {
      if (isTicker) {
        setIsTickerThumbDragging(false);
        setThumbPressed(false);
        getIsTickerThumbDragging && getIsTickerThumbDragging(false);
        const ratio = vertical ? 1 - calculateRatio(currentPos.current).ratio : calculateRatio(currentPos.current).ratio;
        const newValue = calculateValueByPos(ratio * trackLength);
        onChange(e, newValue);
        if (newValue === value) {
          const newPos = calculatePosByValue(value);
          // set scale ratio for both selected and void tracks
          calculateRatio(newPos);
          // set pos for thumb
          setPosition(newPos);
        } else {
          setInternalValue(newValue);
        }
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('pointerup', handlePointerUp);
    }
  };


  const onPointerDownThumb = (e) => {
    e.stopPropagation();
    if (!disabled) {
      if (isTicker) {
        setThumbPressed(true);
        setIsTickerThumbDragging(true);
        getIsTickerThumbDragging && getIsTickerThumbDragging(true);
      }
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('mousemove', handleMouseMove);
    }
  };

  const handleBlur = () => {
    thumbPressed && setThumbPressed(false);
  };

  const handleKeyDown = (e) => {
    if (onKeyDown) {
      onKeyDown(e);
    } else {
      let tempValue = value;
      const shouldDecrease = e.key === KEY_CODES.ARROW_LEFT || e.key === KEY_CODES.ARROW_DOWN;
      const shouldIncrease = e.key === KEY_CODES.ARROW_RIGHT || e.key === KEY_CODES.ARROW_UP;

      if (shouldDecrease) {
        tempValue -= stepBy;
      } else if (shouldIncrease) {
        tempValue += stepBy;
      }

      const normalizedValue = normalizeValue(tempValue);
      if (normalizedValue !== value) {
        onChange(e, normalizedValue);
      }
    }
  };

  const handleMouseEnter = (e) => {
    setIsThumbHover(true);
    onMouseEnter && onMouseEnter(e);
  };

  const handleMouseLeave = (e) => {
    setIsThumbHover(false);
    onMouseLeave && onMouseLeave(e);
  };

  // Prevent the animation for TearDrop when isTicker or disabled is changed
  useEffect(() => {
    if (disabled !== true) {
      if (isTicker === true) {
        teardropAnimation.current = 'transform 0.1s cubic-bezier(0.4, 0, 0.2, 0)';
      } else {
        teardropAnimation.current = 'none';
      }
    } else {
      teardropAnimation.current = 'none';
    }
  }, [disabled, isTicker]);

  const getThumbTransform = () => {
    let transform = `translateX(${!RTL ? '-50%' : '50%'}) translateY(-40%) translateX(${!RTL ? pos : pos - trackLength}px)`;
    if (vertical) {
      transform = `translateX(-50%) translateY(-50%) translateY(${pos}px)`;
    }
    return transform;
  };

  const renderDefaultThumb = internalProps => <ThumbContent internalProps={internalProps} {...internalThumbProps} />;

  const resolvedThumbProps = {
    vertical,
    $theme: resolvedTheme,
    disabled,
    isTicker,
    isThumbHover,
    value: internalValue,
    pos,
    trackLength,
    trackPosition,
    flipIndicatorPosition,
    alwaysShowTeardrop,
    ...otherProps
  };

  const resolvedTearDropProps = {
    ...resolvedThumbProps,
    thumbPressed,
  };

  return (
    <Styled.Thumb
      ref={ref}
      isTickerThumbDragging={isTickerThumbDragging}
      $theme={resolvedTheme}
      $rtl={RTL}
      isTicker={isTicker}
      style={{ transform: getThumbTransform(), ...style }}
      disabled={disabled}
      $disableAnimation={disableAnimation}
      onPointerDown={onPointerDownThumb}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      isThumbHover={isThumbHover}
      vertical={vertical}
      {...wrapperProps}
    >
      {thumbContent !== undefined ? thumbContent(resolvedThumbProps) : renderDefaultThumb(resolvedThumbProps)}
      {teardropContent && teardropContent({
        ...resolvedTearDropProps,
        value: ((internalValue).toString())
      })}

    </Styled.Thumb>

  );
}));

Thumb.defaultProps = {
  thumbContent: undefined,
  teardropContent: renderDefaultTeardrop,
  wrapperProps: {},
  vertical: false,
  getIsTickerThumbDragging: () => {},
  normalizeValue: () => {},
  value: 0,
  style: {},
  theme: {},
  flipIndicatorPosition: false,
  alwaysShowTeardrop: false,
  internalThumbProps: {},
  onKeyDown: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined
};

Thumb.displayName = 'Thumb';

export default Thumb;
